/** Platform-operator console (internal /admin surface): shell, overview, fleet, prospects, audit. */
export const adminOps = {
  shell: {
    title: "Platform admin",
    subtitle: "TrueSmartTech operator console",
    nav: {
      overview: "Overview",
      growth: "Growth",
      customers: "Customers",
      fleet: "Fleet",
      support: "Support",
      prospects: "Prospects",
      orders: "Orders",
      inventory: "Inventory",
      marketplace: "Ad marketplace",
      audit: "Audit log",
    },
  },
  index: {
    title: "Platform admin",
    description:
      "Cross-tenant operations: customers, fleet, sales pipeline and system health.",
    sections: "Sections",
    stats: {
      workspaces: "Workspaces",
      users: "Users",
      devices: "Devices",
      online: "{count} online",
      activeSubscriptions: "Active subscriptions",
      trialing: "Trials running",
      mrrEstimate: "MRR (est.)",
      trialFunnel: "Trial funnel",
      trialFunnelDetail: "{trialing} trialing · {expiring} expiring 7d · {expired} expired 30d",
      devicesOnline: "Devices online",
      devicesOnlineDetail: "{online} of {total}",
      escalatedTickets: "Escalated tickets",
      escalatedTicketsDetail: "Open, awaiting the platform team",
      deviceErrors24h: "Device errors (24h)",
      realtime: "Realtime peers",
      realtimeDetail: "{devices} devices · {controls} controls",
      liveSessions: "Live sessions",
      broadcastSessions: "Broadcasts",
      uptime: "API uptime",
      environment: "Environment",
      envHint: "Presence only. Values are never shown.",
      configured: "set",
      missing: "missing",
      notSet: "not set",
    },
    cards: {
      growth: "See posts and emails as they go out, plus money and the pipeline.",
      customers: "Customers, subscriptions and revenue across every workspace.",
      fleet: "Every device in every workspace: status, owner, license.",
      support: "Tickets escalated to OmniMedia by any workspace.",
      prospects: "Outreach pipeline from the prospects sheet to live venues.",
      orders: "Physical player orders awaiting fulfilment.",
      inventory: "Hardware stock, serials and label printing.",
      marketplace: "Cross-tenant ad-rental bookings oversight.",
      audit: "Who did what on this console, newest first.",
    },
  },
  fleet: {
    title: "Fleet (all workspaces)",
    description:
      "Read-only cross-tenant device list. Manage devices inside their workspace.",
    searchPlaceholder: "Search device, serial, workspace or owner email…",
    filter: { allStatuses: "All statuses" },
    table: {
      device: "Device",
      workspace: "Workspace",
      owner: "Owner",
      status: "Status",
      lastSeen: "Last seen",
      license: "License",
      active: "Active",
      inactive: "Inactive",
      serial: "Serial {serial}",
      noSubscription: "No subscription",
    },
    empty: "No devices match.",
    pagination: "{from}–{to} of {total}",
    previous: "Previous",
    next: "Next",
  },
  prospects: {
    title: "Prospects",
    description:
      "Operator outreach CRM, imported from the marketing prospects sheet.",
    searchPlaceholder: "Search company, city, country or email…",
    filter: { all: "All" },
    status: {
      inbound: "Inbound",
      listed: "Listed",
      contacted: "Contacted",
      replied: "Replied",
      demo: "Demo",
      trial: "Trial",
      agreed: "Agreed",
      provisioned: "Provisioned",
      live: "Live",
      lost: "Lost",
    },
    table: {
      company: "Company",
      segment: "Segment",
      place: "Where",
      contact: "Contact",
      locations: "Venues",
      tier: "Tier",
      status: "Status",
      nextAction: "Next action",
      notes: "Notes",
      actions: "Actions",
    },
    action: {
      add: "Add prospect",
      import: "Import CSV",
      export: "Export CSV",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
    },
    dialog: {
      addTitle: "Add prospect",
      editTitle: "Edit prospect",
      company: "Company",
      segment: "Segment",
      country: "Country",
      city: "City",
      website: "Website",
      contactEmail: "Contact email",
      phone: "Phone",
      locationsEstimate: "Venue estimate",
      tier: "Tier",
      notes: "Notes",
      nextActionAt: "Next action (date)",
      sourceUrl: "Source URL",
    },
    toast: {
      created: "Prospect added",
      updated: "Prospect updated",
      deleted: "Prospect deleted",
      imported: "Imported {imported}. Skipped {skippedDuplicates} duplicates and {skippedInvalid} invalid",
      failed: "Action failed",
      duplicate: "A prospect with this company + country already exists",
    },
    empty: "No prospects yet. Import the outreach CSV to get started.",
    confirmDelete: "Delete this prospect?",
    provision: {
      action: "Provision",
      title: "Create provisioning profile",
      description:
        "The workspace is built automatically, with them as owner, the first time this email signs in.",
      email: "Sign-in email",
      companyName: "Workspace name",
      businessType: "Business type",
      businessTypePlaceholder: "e.g. Café / Coffee shop, Gym, Retail",
      locations: "Locations (one name per line)",
      deviceCount: "Devices (total, license-clamped on claim)",
      openingTime: "Opens (HH:MM)",
      closingTime: "Closes (HH:MM)",
      sendInvite: "Email the prospect that their workspace is waiting",
      submit: "Create profile",
      pendingBadge: "Provisioning pending",
      toast: {
        created:
          "Provisioning profile created. The workspace is built on first sign-in.",
        duplicate: "A pending profile already exists for this email.",
        failed: "Could not create the provisioning profile.",
      },
    },
  },
  support: {
    title: "Support inbox",
    description:
      "Tickets escalated to OmniMedia across every workspace, newest first.",
    table: {
      workspace: "Workspace",
      owner: "Owner",
      subject: "Subject",
      status: "Status",
      priority: "Priority",
      escalated: "Escalated",
      comments: "Replies",
    },
    empty: "No escalated tickets. Nothing needs the platform team right now.",
    detail: {
      openedBy: "Opened by {name}",
      someone: "someone",
      escalatedOn: "Escalated {date}",
      workspaceLabel: "{workspace} · {owner}",
      noReplies: "No replies yet.",
      replyPlaceholder: "Reply as OmniMedia staff…",
      reply: "Reply",
      staffBadge: "Staff",
      commentAuthorFallback: "User",
      reporterFallback: "Reporter",
    },
    toast: {
      replied: "Reply posted",
      replyFailed: "Could not post the reply",
      updated: "Ticket updated",
      updateFailed: "Could not update the ticket",
    },
  },
  audit: {
    title: "Audit log",
    description: "Platform-admin actions across all tenants, newest first.",
    table: {
      when: "When",
      actor: "Actor",
      action: "Action",
      subject: "Subject",
      meta: "Details",
    },
    empty: "No admin actions recorded yet.",
    loadMore: "Load more",
  },
  growth: {
    title: "Growth",
    description:
      "Posts as they are queued, emails as they are sent. Live, not a git file.",
    generatedAt: "Read at {when}",
    product: {
      title: "Product",
      workspaces: "Workspaces",
      devices: "Devices",
      players: "Players connected",
      controls: "Controls connected",
      sha: "Release",
    },
    money: {
      title: "Money",
      paid: "Paid subscriptions",
      trials: "Trials",
      mrr: "MRR (est.)",
    },
    social: {
      title: "Social",
      notConfigured:
        "Post Bridge is not configured on this server. Set POST_BRIDGE_API_KEY on the droplet.",
      error: "Post Bridge error: {message}",
      connected: "{count} of 14 accounts connected",
      missing: "Not connected",
      reconnect: "needs reconnect",
      tabToday: "Today",
      tabQueued: "Queued",
      tabDrafts: "Drafts",
      tabLive: "Live",
      tabFailed: "Failed",
      empty: "Nothing in this list.",
      when: "When",
      where: "Where",
      caption: "Caption",
      firstReply: "First reply",
      cest: "CEST",
    },
    outreach: {
      title: "Outreach",
      empty: "No prospects imported yet. Use Prospects to import the CSV.",
    },
    preview: {
      posts: "Posts",
      emails: "Emails",
      board: "Board",
      filterAll: "All brands",
      queued: "Queued",
      draft: "Draft",
      live: "Live",
      failed: "Failed",
      processing: "Sending",
      mediaGone: "This post had media. The file is no longer in Post Bridge.",
      noCaption: "No caption.",
      prevImage: "Previous image",
      nextImage: "Next image",
      imageN: "Image {n}",
      approve: "Approve",
      skip: "Don't post",
      edit: "Edit",
      save: "Save",
      cancel: "Cancel",
      time: "Time (CEST)",
      firstReply: "First reply on X",
      toastApproved: "Approved. It will go out at the scheduled time.",
      toastUpdated: "Saved",
      toastDeleted: "Removed from the queue",
      toastFailed: "Could not save: {message}",
      weekHint: "Times are CEST. Approve a draft to put it on the clock.",
      selectAll: "Select all",
      selectedN: {
        one: "{count} selected",
        other: "{count} selected",
      },
      approveSelected: "Approve selected",
      approveDay: "Approve this day",
      skipSelected: "Don't post selected",
      confirmSkipTitle: "Remove these posts?",
      confirmSkipBody: {
        one: "{count} post will be deleted from the queue. A live post is never touched.",
        other:
          "{count} posts will be deleted from the queue. A live post is never touched.",
      },
      toastApprovedN: {
        one: "Approved {count} post. It will go out at the scheduled time.",
        other: "Approved {count} posts. They will go out at the scheduled time.",
      },
      toastPartial: "{ok} saved, {failed} failed.",
      markReadySelected: "Ready selected",
      skipEmailSelected: "Skip selected",
      toastEmailReadyN: {
        one: "Marked {count} email ready. Resend sends it at the CEST time.",
        other: "Marked {count} emails ready. Resend sends them at the CEST time.",
      },
      toastEmailSkippedN: {
        one: "Skipped {count} email for this day.",
        other: "Skipped {count} emails for this day.",
      },
      sendHint:
        "Ready puts the email on Resend. It goes out at the CEST time. You do not send it by hand.",
      sequence: "Which email",
      emailN: "Email {n}",
      previewAs: "Fill as",
      noProspect: "Pick a venue",
      tokensVisible: "Keep placeholders",
      from: "From",
      to: "To",
      subject: "Subject",
      copySubject: "Copy subject",
      copyBody: "Copy body",
      copied: "Copied",
      noEmails: "No outreach emails are loaded.",
      mondayList: "This week's sends",
      daySends: "{day}",
      skipEmail: "Skip this Monday",
      markReady: "Ready to send",
      ready: "Ready",
      emailQueued: "On the clock",
      emailSent: "Sent",
      skipped: "Skipped",
      toastEmailReady: "Marked ready. Resend sends it at the CEST time.",
      toastEmailSkipped: "Skipped for this Monday.",
      seeOmniMedia: "See OmniMedia",
    },
  },
  common: {
    loadError: "Could not load. Are you a platform admin?",
    notAdminHint:
      "This console requires your email to be in PLATFORM_ADMIN_EMAILS.",
  },
} as const;
